import type { AuthState } from "@/types/developer";
import { getCsrfToken, invalidateCsrfToken } from "./csrf";

const BASE = "/api";

async function post<T>(path: string, body: unknown): Promise<T> {
  const csrfToken = await getCsrfToken();
  const res = await fetch(BASE + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok)
    throw new Error(
      (data as { error?: string }).error ?? `Auth ${path}: ${res.status}`,
    );
  return data as T;
}

export async function fetchMe(): Promise<AuthState> {
  const res = await fetch(BASE + "/auth/me", { credentials: "include" });
  return res.json() as Promise<AuthState>;
}

export async function adminLogin(
  password: string,
): Promise<{ role?: string; totp_required?: boolean }> {
  return post("/auth/admin-login", { password });
}

export async function adminTotpVerify(code: string): Promise<{ role: string }> {
  return post("/auth/admin-totp-verify", { code });
}

export async function adminSessionStatus(): Promise<{
  active: boolean;
  remaining_sec?: number;
  reason?: string;
}> {
  const res = await fetch(BASE + "/auth/admin-session-status", {
    credentials: "include",
  });
  return res.json();
}

export async function adminTotpSetup(): Promise<{
  secret: string;
  uri: string;
}> {
  return post("/auth/admin-totp-setup", {});
}

export async function adminTotpConfirm(code: string): Promise<{ ok: boolean }> {
  return post("/auth/admin-totp-confirm", { code });
}

export async function devLogin(
  email: string,
  password: string,
): Promise<AuthState> {
  return post<AuthState>("/auth/dev-login", { email, password });
}

export async function register(token: string, password: string): Promise<void> {
  await post("/auth/register", { token, password });
}

export async function logout(): Promise<void> {
  await post("/auth/logout", {});
  invalidateCsrfToken();
}

export async function forgotPassword(email: string): Promise<void> {
  await post("/auth/forgot-password", { email });
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  await post("/auth/reset-password", { token, password });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export async function deleteMyAccount(): Promise<void> {
  await post("/dev/account/delete", {});
}

export async function setupTotp(): Promise<{
  secret: string;
  otp_url: string;
}> {
  return post("/auth/totp/setup", {});
}

export async function confirmTotp(code: string): Promise<void> {
  await post("/auth/totp/confirm", { code });
}

export async function disableTotp(currentPassword: string): Promise<void> {
  await post("/auth/totp/disable", { current_password: currentPassword });
}

export async function loginTotp(code: string): Promise<void> {
  await post("/auth/totp/login", { code });
}

export async function loginTotpBackupCode(code: string): Promise<void> {
  await post("/auth/totp/backup-code-login", { code });
}

export async function generateBackupCodes(): Promise<{ codes: string[] }> {
  return post<{ codes: string[] }>("/auth/totp/backup-codes", {});
}

// BY: email verification
export async function verifyEmail(token: string): Promise<{ message: string }> {
  return post<{ message: string }>("/auth/verify-email", { token });
}

export async function resendVerification(
  email: string,
): Promise<{ message: string }> {
  return post<{ message: string }>("/auth/resend-verification", { email });
}

// CC: session management
export interface DevSession {
  id: number;
  ip_address: string | null;
  user_agent: string | null;
  last_seen_at: string;
  created_at: string;
  is_current: boolean;
}

export async function fetchSessions(): Promise<{ data: DevSession[] }> {
  const res = await fetch("/api/auth/sessions", { credentials: "include" });
  const data = await res.json();
  if (!res.ok)
    throw new Error(
      (data as { error?: string }).error ?? `GET /auth/sessions: ${res.status}`,
    );
  return data as { data: DevSession[] };
}

export async function revokeSession(id: number): Promise<void> {
  const csrfToken = await getCsrfToken();
  const res = await fetch("/api/auth/sessions", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ id }),
  });
  const data = await res.json();
  if (!res.ok)
    throw new Error(
      (data as { error?: string }).error ??
        `DELETE /auth/sessions: ${res.status}`,
    );
}
